// The three honesty rules, turned into sentences.
//
// 🔴 THE THIRD RULE GREW A SECOND HALF IN T10 AND KEPT ITS FIRST ONE.
// This file's header used to end with a paragraph headed "CURRENCIES ARE NEVER
// SUMMED. THERE IS NO CONVERSION UNTIL T10." The first sentence is still true
// and the second is spent. What replaced it is below, in `honestRollUp`: a
// converted figure arrives from `trips/utils/convert.py` **beside** the
// per-currency list, carrying the rate's as-of date, and nothing in this
// module reduces the list into it.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE ARITHMETIC IS NOT HERE, AND MUST NOT MOVE HERE
// ═══════════════════════════════════════════════════════════════════════════
//
// Every number this module renders was computed by trips/utils/cost.py and
// arrived over the wire. Nothing in this file adds, divides, or compares an
// amount. That is deliberate and it is the one rule to keep when editing:
// the estimate is "the misprice-a-trip risk", and a second implementation of
// it in a renderer is how the board and the service start disagreeing about
// what a trip costs — with the board being the half people actually read.
//
// What this file does is the half the service explicitly left to us: "This
// module returns the data; the sentences are the front end's."
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 CURRENCIES ARE STILL NEVER SUMMED HERE — AND NOW THERE IS A CONVERSION
// ═══════════════════════════════════════════════════════════════════════════
//
// `RollUpRead` carries no single per-person total, on purpose — "a scalar
// `total` field would be a place for that mistake to be made, so the shape does
// not offer one." This module inherits that: `subtotals` is rendered as a LIST,
// one labeled figure per currency, and **there is still no code path in this
// file that reduces it.** The spec records what that prevents — an estimate
// that "would have silently added ¥ to £".
//
// What T10 added is a DIFFERENT NUMBER, computed somewhere else. The converted
// figure comes from `trips/utils/convert.py`, arrives on
// `/api/trips/:id/enrichment`, and carries the rate's as-of date as a required
// field. It is rendered beside the list, never instead of it — `utils/cost.py`
// wrote that constraint before this slice existed: *"it adds a converted figure
// alongside these … it does not replace them, and it does not get to delete
// this list."*
//
// ✅ THE MOCK'S SINGLE FIGURE IS NOW BUILDABLE, WITH THE LABEL IT ALWAYS
// NEEDED. `Trip Board.dc.html` renders `≈ £2,317` with "€ converted 16 Apr"
// beside it — the "16 Apr" is the as-of date, and this file's older comment
// called that render "the finished product — T10". The pairing is not
// decoration: a converted figure without its date is precisely the number this
// product spent nine slices refusing to print.

import { conversionFigure, currencyLine } from './enrichment.js'

/** Display currency (per-trip) is the TARGET of the conversion, and it is
 * STILL never the unit of a figure in `subtotals`. The service says so on the
 * field itself: "a client that renders this as the unit of the numbers would
 * be wrong."
 *
 * The one figure it IS the unit of is `conversion.figure`, which carries its
 * own `target_currency` rather than inheriting this one — so a client cannot
 * label a converted number with a currency the conversion did not use. */

/** Format an amount the service sent, in the currency it sent it in.
 *
 * Amounts arrive as strings (pydantic serialises `Decimal` to a string in JSON
 * mode) and are converted to a number HERE, for `Intl` and nothing else. The
 * store counts in `NUMERIC(14, 2)`; this is the renderer formatting.
 *
 * `Intl` also settles the minor-unit question the service deliberately refused:
 * cost.py quantises ¥ to "12000.00" because a per-currency minor-unit table is
 * "data this service does not have and would have to guess at". The browser has
 * that table. So ¥12,000 renders with no decimals and £1,840.50 with two,
 * without anybody hardcoding a list.
 */
export function money(amount, currency) {
  const value = typeof amount === 'number' ? amount : Number.parseFloat(amount)
  if (!Number.isFinite(value)) return null
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(value)
  } catch {
    // 🔴 MEASURED, because the obvious assumption about this branch is wrong.
    // Intl does NOT reject an unknown currency: any three ASCII letters are
    // accepted and the code is used as the symbol, so 'ZZZ' renders as
    // "ZZZ 10.00" through the path above and never reaches here. What actually
    // throws is a MALFORMED code — 'GB', 'G1P', '' — all RangeError.
    //
    // The service's pattern is `^[A-Za-z]{3}$`, so this should be unreachable
    // through the API. It exists because a renderer that throws takes the whole
    // board down over one bad field, and a board that renders is worth more
    // than a board that is right about a currency code.
    return `${value.toFixed(2)} ${currency}`
  }
}

/** "at 6 people" — rule one, always visible. */
export function headcountLabel(headcount) {
  const n = Number(headcount)
  if (!Number.isFinite(n) || n < 1) return null
  return `at ${n} ${n === 1 ? 'person' : 'people'}`
}

/** "amounts on 8 of 12 entries" — rule two, always visible.
 *
 * The zero case is worded rather than hidden. "amounts on 0 of 12 entries"
 * reads as a bug; "nothing priced yet" is the same fact in the register this
 * repo asks for, and it is what the mock's category rows say.
 */
export function coverageLabel(rollUp) {
  const total = Number(rollUp?.entries_total ?? 0)
  const priced = Number(rollUp?.entries_priced ?? 0)
  if (total === 0) return 'nothing on the board yet'
  if (priced === 0) return `nothing priced yet · ${total} ${total === 1 ? 'entry' : 'entries'}`
  return `amounts on ${priced} of ${total} ${total === 1 ? 'entry' : 'entries'}`
}

/**
 * One labeled per-person figure per currency — rule three, always visible.
 *
 * 🔴 Returns a LIST and there is STILL no variant that returns a total, which
 * survives T10 unchanged and is worth saying now that a converted figure
 * exists: the answer to *"what does this two-currency trip cost each"* is a
 * number **the service** computed against a dated rate, and it arrives on a
 * different endpoint. It is not something a caller may derive from this list,
 * because deriving it would mean choosing a rate in a renderer.
 */
export function perCurrencyFigures(rollUp) {
  const subtotals = Array.isArray(rollUp?.subtotals) ? rollUp.subtotals : []
  return subtotals
    .map((subtotal) => ({
      currency: subtotal.currency,
      each: money(subtotal.per_person, subtotal.currency),
      // The unformatted decimal string the service sent, carried alongside the
      // rendered one. P5-3 T9's column delta needs to subtract two figures in
      // whole minor units, and re-parsing "£306.67" back out of a formatted
      // string would mean reading a locale's own output — narrow symbols, group
      // separators, and a minus sign that is not always U+002D. The value that
      // arrived is the one to do arithmetic on; `each` is for eyes.
      raw: subtotal.per_person,
      perPersonComponent: money(subtotal.per_person_component, subtotal.currency),
      flatComponent: money(subtotal.flat_component, subtotal.currency),
      perPersonEntryCount: Number(subtotal.per_person_entry_count ?? 0),
      flatEntryCount: Number(subtotal.flat_entry_count ?? 0),
    }))
    .filter((figure) => figure.each !== null)
}

/**
 * "cheapest stay used — nothing's chosen here yet".
 *
 * 🔴 HANDOFF's cost-formula refinement, and the slice plan says it "is
 * authoritative where this spec is coarser": flat amounts count only the CHOSEN
 * option in a category where one is chosen, and where nothing is chosen the UI
 * must STATE WHICH BASIS WAS USED. Not showing this line is not a smaller
 * version of the feature — it is the board quoting a number whose derivation it
 * has hidden, which is the one thing this product is against.
 *
 * The service hands over everything the sentence needs (`FlatSelectionRead`:
 * category, currency, rule, and the titles it used) precisely so the wording is
 * a design decision made here rather than a string baked into an API.
 *
 * The currency is named only when the roll-up spans more than one, because
 * "cheapest stay used" is unambiguous on a single-currency trip and
 * "cheapest stay in GBP used" is noise on one.
 */
export function basisNotes(rollUp) {
  const selections = Array.isArray(rollUp?.flat_selection) ? rollUp.flat_selection : []
  const multiCurrency = (rollUp?.currencies?.length ?? 0) > 1

  return selections.map((selection) => {
    const where = multiCurrency ? ` (${selection.currency})` : ''
    const titles = Array.isArray(selection.entry_titles) ? selection.entry_titles : []

    if (selection.rule === 'chosen') {
      return {
        key: `${selection.category}-${selection.currency}`,
        text:
          titles.length === 1
            ? `chosen ${selection.category} only${where} — the others aren’t counted`
            : `${titles.length} chosen ${selection.category} entries counted${where}`,
        titles,
        rule: 'chosen',
      }
    }

    return {
      key: `${selection.category}-${selection.currency}`,
      text: `cheapest ${selection.category} used${where} — nothing’s chosen here yet`,
      titles,
      rule: 'cheapest',
    }
  })
}

/**
 * Everything one roll-up needs to render honestly, in one call.
 *
 * ⚠️ `figures` may be empty, and an empty list is a real state rather than a
 * missing one: a board where nobody has typed an amount has no per-person
 * figure, and inventing "≈ £0 each" would be the board claiming a completeness
 * it does not have. The caller renders the coverage line either way, which is
 * what says so.
 */
export function honestRollUp(rollUp, headcount, conversionSection) {
  const figures = perCurrencyFigures(rollUp)
  // 🔴 T10 FILLED THE SEAM THE LINE ABOVE THIS ONE PROMISED, AND CHANGED
  // NOTHING ELSE IN THIS FILE. `conversion` was `null` here with the comment
  // *"named rather than silently absent so the next slice has an obvious
  // seam"*, and this is that slice using it as intended: an extra field
  // beside the list, never a reduction of it.
  //
  // `perCurrencyFigures` still returns a LIST and there is still no function
  // in this module that returns a single total. The converted figure is
  // computed by `trips/utils/convert.py` and arrives over the wire — so the
  // rule this file opens with holds unchanged: nothing here adds, divides or
  // compares an amount.
  const conversion = conversionFigure(conversionSection, money)
  return {
    headcount: headcountLabel(headcount),
    coverage: coverageLabel(rollUp),
    figures,
    notes: basisNotes(rollUp),
    conversion,
    // The third honesty rule's sentence. It used to be built in the component
    // and had exactly one wording; there are three now, and which one is right
    // depends on data the component should not be inspecting.
    currency: currencyLine(figures, conversion),
  }
}

/** The four fixed categories, in the order the board shows them.
 *
 * Fixed, per the spec's carried-over ruling: "typed categories (stay · eat ·
 * do · travel, fixed)". The roman numerals are the mock's row markers.
 */
export const CATEGORIES = [
  { key: 'stay', label: 'stay', numeral: 'I', addLabel: 'a stay' },
  { key: 'eat', label: 'eat', numeral: 'II', addLabel: 'somewhere to eat' },
  { key: 'do', label: 'do', numeral: 'III', addLabel: 'something to do' },
  { key: 'travel', label: 'travel', numeral: 'IV', addLabel: 'a way to get there' },
]

/** A category row's one-line subtotal, matching the mock's header. */
export function categorySubtotal(rollUp) {
  const figures = perCurrencyFigures(rollUp)
  if (figures.length === 0) return 'nothing priced yet'
  const each = figures.map((figure) => `≈ ${figure.each} each`).join(' · ')
  return `${each} · ${coverageLabel(rollUp)}`
}
